import { useSaveData } from '@entities/common/savedData/index';
import { yupResolver } from '@hookform/resolvers/yup';
import qs from 'query-string';
import { useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation, useParams } from 'react-router-dom';
import { useDescriptionEditor } from '@widgets/shared/descriptionEditor/model.ts';
import { useExerciseEditFormPagesRedirect } from '@features/exercises/exercise-form/model/helpres.ts';
import { useModuleByCodeParam } from '@shared/hooks/use-module-by-code-param.ts';
import { usePartitionsWithTagsSelection } from '@shared/hooks/use-partitions-with-tags-selection.ts';
import { useCloseFormDialogModal } from '@shared/hooks/useCloseFormDialogModal/useCloseFormDialogModal.ts';
import { type ExerciseVideo } from '@shared/types/exercises';
import { useExerciseQuery } from '@src/api/api-hooks/exercises/useExerciseQuery.ts';
import { exerciseCreateSchema, exerciseUpdateSchema, type ExerciseFormValues } from '../lib';

const getParsedExercise = (exercise: ExerciseVideo) => ({
  title: exercise?.title,
  description: exercise?.description,
  tags: exercise.tags ? exercise.tags.map((tag) => tag.id) : [],
  screenshot_url: exercise?.screenshot_url,
  video_type: exercise?.video_type,
  url: exercise?.embed_url || exercise?.metadata?.FileName,
  module: exercise?.module,
  partitions: exercise?.partitions.map((p) => p.id),
});
// const defaultFormState = {
//   file: '',
//   title: '',
//   description: '',
//   tags: [],
//   video_type: 'B' as 'B' | 'Y' | undefined,
//   module: 0,
// };

export const useExerciseForm = (isTempExercise: boolean) => {
  const { state } = useLocation();
  const { code, moduleId } = useModuleByCodeParam();
  const { videoId } = useParams();
  const exerciseId = Number(videoId);
  const { doCloseFormWithDialogModal } = useCloseFormDialogModal();
  const { doRedirectOnPrevPage } = useExerciseEditFormPagesRedirect();
  const { data: exercise, isLoading: isExerciseLoading } = useExerciseQuery({
    id: videoId === 'new' ? undefined : videoId,
  });

  const _HASH = `ProgramExerciseFormHash${exerciseId}`;
  const { clearStorage: clearDescription } = useDescriptionEditor(_HASH);

  const { source } = qs.parse(location.search);

  const {
    data: savedData,
    doRemoveStorageData,
    doSetStorageData,
  } = useSaveData<ExerciseFormValues & { isDisabledSelectionFromLibrary: boolean }>(
    'exerciseEditorFormState'
  );

  const { clearPartitionsData, selectedPartitions, selectedTags } = usePartitionsWithTagsSelection({
    hash: _HASH,
    initializeData: {
      tags: exercise?.tags || [],
      partitions: exercise?.partitions || [],
      savedFormState: savedData,
    },
  });
  // console.log("exercise:", exercise);
  // console.log("savedData:", savedData);
  // console.log('selectedPartitions:', selectedPartitions);

  // const module = moduleById(exercise?.module);

  //Если вернулись со страницы выбора тегов/разделов то передаем false, что будет означать "НЕ ГРУЗИТЬ ТЕГИ!!!"

  // const { state } = useLocation();

  // const needToLoadTags = !isTempExercise || !state?.wasSelecting;

  // useLoadTags(selectedModule?.code, needToLoadTags);
  // console.log("isTempExercise:", isTempExercise);
  // console.log('selectedModule:', selectedModule);
  // console.log('ex:', exercise);
  // useEffect(() => {
  //   typeof storageData === 'undefined' &&
  //     doSetStorageData({
  //       ...defaultFormState,
  //       module: selectedModule?.id || exercise?.module || 0,
  //     });
  // }, [storageData, doSetStorageData, selectedModule, exercise]);

  // const defaultData = storageData || exercise || defaultFormState;
  const defaultData =
    (savedData && {
      ...savedData,
      module: moduleId,
      tags: selectedTags,
      partitions: selectedPartitions,
    }) ||
    (exercise && {
      ...getParsedExercise(exercise),
    });
  // || {
  //   file: '',
  //   title: '',
  //   description: '',
  //   tags: selectedTagIds,
  //   video_type: 'B' as 'B' | 'Y' | undefined,
  //   module: selectedModule?.id || 0,
  // };

  const { reset, ...methods } = useForm<ExerciseFormValues>({
    resolver: yupResolver(!exercise ? exerciseCreateSchema : exerciseUpdateSchema) as any,
    defaultValues: {
      file: '',
      title: '',
      description: '',
      tags: selectedTags,
      partitions: selectedPartitions,
      video_type: 'B' as 'B' | 'Y' | undefined,
      module: moduleId,
    },
    values: { ...defaultData },
  });

  const clearStorage = useCallback(() => {
    setTimeout(() => {
      clearPartitionsData();
      clearDescription();
      doRemoveStorageData();

      localStorage.removeItem('exerciseEditorFormState');
      localStorage.removeItem('effectExecuted');
    }, 0);
  }, [clearPartitionsData, doRemoveStorageData, clearDescription]);

  const hasDirtyFields = useMemo(() => {
    const {
      getValues,
      formState: { dirtyFields },
    } = { ...methods };
    const module = getValues('module');
    const isDirty = Object.keys(dirtyFields).length > 0;
    return module !== 0 || isDirty;
  }, [methods]);

  const hasTouchedFields = useMemo(() => {
    const {
      formState: { touchedFields },
    } = { ...methods };

    return Object.keys(touchedFields).length > 0;
  }, [methods]);

  const resetSelectedVideo = useCallback(() => {
    methods.setValue('file', '');
    methods.setValue('url', '');
  }, [methods]);

  const doClose = useCallback(() => {
    clearStorage();
    doRedirectOnPrevPage(`/programs/${code}/exercises`);
  }, [clearStorage, doRedirectOnPrevPage]);

  const isDisabledSelectionFromLibrary = useMemo(
    () =>
      Boolean(state?.isDisabledSelectionFromLibrary || savedData?.isDisabledSelectionFromLibrary),
    [state, savedData]
  );

  const doSaveFormData = useCallback(
    () =>
      doSetStorageData({
        ...methods.getValues(),
        isDisabledSelectionFromLibrary,
      } as any),
    []
  );

  // const onFormSubmit = useCallback(() =>
  //   // id: number
  //   {
  //     // navigate(`exercise/${id}`, { state: { isRedirectAfterCreating: true } });
  //     clearStorage();
  //     navigate(-1);
  //   }, [navigate]);

  return {
    isExerciseLoading,
    exerciseId,
    source,
    defaultData,
    methods: { reset, ...methods },
    hasDirtyFields,
    hasTouchedFields,
    onFormClose: doCloseFormWithDialogModal({
      onModalConfirm: doClose,
      isShouldModalOpen: hasDirtyFields,
    }),
    onFormSubmit: doClose,
    resetSelectedVideo,
    doSaveFormData,
    _HASH,
    isDisabledSelectionFromLibrary,
  };
};
